import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ToastOptions, useToast } from '../../../../lib/chakraUi/hooks';
import { AnyDocument, IncludeParams, Nullable } from '../../../../types';
import { createParams } from '../../../../utils/queries';
import { useRequestConstructor } from '../../../hooks/useRequestConstructor';
import { getApiErrorMessage } from '../../../logic';
import { unwrapNonPaginatedResponseDataAs } from '../../../logic/unwrapNonPaginatedResponseDataAs';
import { ApiErrorResponse } from '../../../types';
import { NonPaginatedResponse } from '../../../types/NonPaginatedResponse';
import { getDocumentQueryKey } from '../../logic/getDocumentQueryKey';
import { updateDocument } from '../../requests/updateDocument';
import { DocumentParams } from '../../types/DocumentParams';

export const useDocumentUpdate = <
  DocumentType extends AnyDocument = AnyDocument,
  Payload = Record<string, unknown>,
>(
  documentParams: DocumentParams,
  documentId: DocumentType['id'],
  include?: string[],
  successMessage: Nullable<ToastOptions> = {
    title: 'Success',
    description: 'Update successful',
  },
) => {
  const { constructPatch } = useRequestConstructor();
  const queryClient = useQueryClient();
  const toast = useToast();

  const mergedParams = useMemo(
    () => ({
      ...createParams<IncludeParams>({ include }),
    }),
    [include],
  );
  const queryKey = getDocumentQueryKey<DocumentType>(
    documentId,
    documentParams,
    mergedParams,
  );

  const { data, mutateAsync, ...rest } = useMutation<
    Awaited<ReturnType<typeof updateDocument<DocumentType, Payload>>>,
    ApiErrorResponse,
    Payload
  >({
    mutationKey: queryKey,
    mutationFn: async (payload) =>
      updateDocument<DocumentType, Payload>(
        constructPatch,
        documentParams,
        documentId,
        mergedParams,
        payload,
      ),
    onError: (error) => {
      toast({
        title: 'Error!',
        description: getApiErrorMessage(error, 'Update failed'),
        type: 'error',
      });
    },
    onSuccess: (response) => {
      const document = response?.data;
      if (!document) return;
      queryClient.setQueryData<NonPaginatedResponse<DocumentType>>(queryKey, {
        data: document,
      });
      if (successMessage) {
        toast({
          ...successMessage,
          type: 'success',
        });
      }
    },
  });

  return {
    ...unwrapNonPaginatedResponseDataAs(data, 'document'),
    ...rest,
    updateDocument: mutateAsync,
  };
};
